import { create } from 'zustand';
import type { StyleOption } from '../../../shared/api/contracts/styleTransferSchemas';
import {
  clearAllEntries,
  deleteEntries,
  deleteEntry as deleteEntryFromDatabase,
  estimateTotalBytes,
  isQuotaError,
  loadAllEntries,
  saveEntry as saveEntryToDatabase
} from '../storage/historyDatabase';

export type CropUnit = 'mm' | 'cm' | 'in';

export interface HistoryResultMetadata {
  crop?: {
    countryCode: string;
    presetId?: string | null;
    presetLabel?: string | null;
    unit: CropUnit;
    sizeMm: {
      width: number;
      height: number;
    };
    dpi: number;
    updatedAt: string;
  };
}

export interface HistoryEntry {
  id: string;
  tokenType: 'device' | 'user';
  style: Pick<StyleOption, 'id' | 'name' | 'category' | 'previewUrl'>;
  original: {
    fileName: string;
    dataUrl: string;
    width: number;
    height: number;
  };
  result: {
    dataUrl: string;
    width: number;
    height: number;
    completedAt: string;
    originalDataUrl?: string;
    originalWidth?: number;
    originalHeight?: number;
    metadata?: HistoryResultMetadata;
  };
  createdAt: string;
  storageBytes: number;
}

export interface HistoryFilters {
  styleId: string | 'all';
  date: string | 'all';
}

export interface HistoryState {
  entries: HistoryEntry[];
  filters: HistoryFilters;
  isInitialized: boolean;
  storageNotice: 'quota' | 'error' | null;
  storageBytes: number;
  storageLimitBytes?: number;
  addEntry: (entry: HistoryEntry) => void;
  clear: () => void;
  removeEntry: (id: string) => void;
  setFilters: (filters: Partial<HistoryFilters>) => void;
  initialize: () => Promise<void>;
  acknowledgeStorageNotice: () => void;
  updateEntry: (id: string, updater: (entry: HistoryEntry) => HistoryEntry | null) => Promise<void>;
}

const MAX_HISTORY_ENTRIES = 50;
const DEFAULT_STORAGE_LIMIT_BYTES = 50 * 1024 * 1024;
const LEGACY_STORAGE_KEY = 'style-transfer-history-v1';
const HISTORY_SESSION_FLAG = 'photo-studio-history-session-active';

let initializationPromise: Promise<void> | null = null;

interface LegacyPersistedState {
  state?: {
    entries?: Array<Omit<HistoryEntry, 'storageBytes'>>;
  };
}

function estimateDataUrlBytes(dataUrl: string | undefined | null): number {
  if (!dataUrl) {
    return 0;
  }
  const commaIndex = dataUrl.indexOf(',');
  const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  const paddingMatch = base64.match(/=+$/);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function normalizeLegacyEntry(entry: Omit<HistoryEntry, 'storageBytes'> | null | undefined): HistoryEntry | null {
  if (!entry) {
    return null;
  }
  const storageBytes = estimateDataUrlBytes(entry.original?.dataUrl) + estimateDataUrlBytes(entry.result?.dataUrl);
  return ensureResultDefaults({
    ...(entry as HistoryEntry),
    storageBytes
  });
}

function ensureResultDefaults(entry: HistoryEntry): HistoryEntry {
  if (entry.result.originalDataUrl && entry.result.metadata) {
    return entry;
  }
  return {
    ...entry,
    result: {
      ...entry.result,
      originalDataUrl: entry.result.originalDataUrl ?? entry.result.dataUrl,
      originalWidth: entry.result.originalWidth ?? entry.result.width,
      originalHeight: entry.result.originalHeight ?? entry.result.height,
      metadata: entry.result.metadata
    }
  };
}

async function migrateLegacyEntries(): Promise<HistoryEntry[]> {
  if (typeof window === 'undefined') {
    return [];
  }
  const legacyPayload = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacyPayload) {
    return [];
  }

  try {
    const parsed = JSON.parse(legacyPayload) as LegacyPersistedState;
    const legacyEntries = Array.isArray(parsed?.state?.entries) ? parsed.state.entries : [];
    const normalized = legacyEntries
      .map((entry) => normalizeLegacyEntry(entry))
      .filter((entry): entry is HistoryEntry => Boolean(entry));

    for (const entry of normalized) {
      try {
        await saveEntryToDatabase(entry);
      } catch (error) {
        console.warn('Failed to migrate legacy history entry', error);
      }
    }

    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    return normalized;
  } catch (error) {
    console.warn('Failed to parse legacy history state', error);
    return [];
  }
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  entries: [],
  filters: { styleId: 'all', date: 'all' },
  isInitialized: false,
  storageNotice: null,
  storageBytes: 0,
  storageLimitBytes: DEFAULT_STORAGE_LIMIT_BYTES,
  initialize: async () => {
    if (get().isInitialized) {
      return;
    }
    if (initializationPromise) {
      return initializationPromise;
    }

    initializationPromise = (async () => {
      try {
        await migrateLegacyEntries();
        const storedEntries = await loadAllEntries();
        const trimmed = storedEntries.slice(0, MAX_HISTORY_ENTRIES).map((entry) => ensureResultDefaults(entry));
        const totalBytes = estimateTotalBytes(trimmed);
        const limit = get().storageLimitBytes ?? DEFAULT_STORAGE_LIMIT_BYTES;
        set((state) => ({
          entries: trimmed,
          filters: state.filters,
          isInitialized: true,
          storageBytes: totalBytes,
          storageNotice: totalBytes > limit ? 'quota' : null
        }));
      } catch (error) {
        console.warn('Failed to load history entries', error);
        set((state) => ({
          entries: state.entries,
          isInitialized: true,
          storageNotice: isQuotaError(error) ? 'quota' : 'error'
        }));
      }
    })();

    await initializationPromise;
  },
  acknowledgeStorageNotice: () => set({ storageNotice: null }),
  addEntry: (entry) => {
    const normalizedEntry = ensureResultDefaults(entry);
    const existingEntries = get().entries;
    const nextEntries = [normalizedEntry, ...existingEntries];
    const trimmedEntries = nextEntries.slice(0, MAX_HISTORY_ENTRIES);
    const removedEntries = nextEntries.length > MAX_HISTORY_ENTRIES ? nextEntries.slice(MAX_HISTORY_ENTRIES) : [];
    const limit = get().storageLimitBytes ?? DEFAULT_STORAGE_LIMIT_BYTES;
    const totalBytes = estimateTotalBytes(trimmedEntries);
    const previousNotice = get().storageNotice;
    const nextNotice = totalBytes > limit ? 'quota' : previousNotice === 'quota' ? null : previousNotice;

    set({ entries: trimmedEntries, storageBytes: totalBytes, storageNotice: nextNotice });

    void (async () => {
      try {
        await saveEntryToDatabase(normalizedEntry);
        if (removedEntries.length) {
          await deleteEntries(removedEntries.map((item) => item.id));
        }
        const currentEntries = get().entries;
        const updatedBytes = estimateTotalBytes(currentEntries);
        const currentLimit = get().storageLimitBytes ?? DEFAULT_STORAGE_LIMIT_BYTES;
        const currentNotice = get().storageNotice;
        const revisedNotice = updatedBytes > currentLimit ? 'quota' : currentNotice === 'quota' ? null : currentNotice;
        set({ storageBytes: updatedBytes, storageNotice: revisedNotice });
      } catch (error) {
        console.warn('Failed to persist history entry', error);
        set({ storageNotice: isQuotaError(error) ? 'quota' : 'error' });
      }
    })();
  },
  clear: () => {
    set({ entries: [], storageBytes: 0, storageNotice: null });
    void (async () => {
      try {
        await clearAllEntries();
      } catch (error) {
        console.warn('Failed to clear stored history', error);
        set({ storageNotice: isQuotaError(error) ? 'quota' : 'error' });
      }
    })();
  },
  removeEntry: (id) => {
    set((state) => {
      const nextEntries = state.entries.filter((entry) => entry.id !== id);
      const totalBytes = estimateTotalBytes(nextEntries);
      const limit = state.storageLimitBytes ?? DEFAULT_STORAGE_LIMIT_BYTES;
      const nextNotice = totalBytes > limit ? 'quota' : state.storageNotice === 'quota' ? null : state.storageNotice;
      return {
        entries: nextEntries,
        storageBytes: totalBytes,
        storageNotice: nextNotice
      };
    });
    void deleteEntryFromDatabase(id).catch((error) => {
      console.warn('Failed to delete history entry', error);
      set({ storageNotice: isQuotaError(error) ? 'quota' : 'error' });
    });
  },
  updateEntry: async (id, updater) => {
    const currentEntries = get().entries;
    const entryIndex = currentEntries.findIndex((entry) => entry.id === id);
    if (entryIndex < 0) {
      return;
    }
    const sourceEntry = currentEntries[entryIndex];
    const draftEntry = updater(sourceEntry);
    if (!draftEntry) {
      return;
    }
    const updatedEntry = ensureResultDefaults(draftEntry);
    const nextEntries = [...currentEntries];
    nextEntries[entryIndex] = updatedEntry;
    const limit = get().storageLimitBytes ?? DEFAULT_STORAGE_LIMIT_BYTES;
    const totalBytes = estimateTotalBytes(nextEntries);
    const previousNotice = get().storageNotice;
    const nextNotice = totalBytes > limit ? 'quota' : previousNotice === 'quota' ? null : previousNotice;
    set({
      entries: nextEntries,
      storageBytes: totalBytes,
      storageNotice: nextNotice
    });
    try {
      await saveEntryToDatabase(updatedEntry);
    } catch (error) {
      console.warn('Failed to update history entry', error);
      set({ storageNotice: isQuotaError(error) ? 'quota' : 'error' });
    }
  },
  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters }
    }))
}));

function hasActiveHistorySession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(HISTORY_SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

function markHistorySession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(HISTORY_SESSION_FLAG, '1');
  } catch {
    // sessionStorage 可能不可用，忽略即可
  }
}

async function resetHistoryForNewSession(): Promise<void> {
  if (hasActiveHistorySession()) {
    return;
  }

  useHistoryStore.setState((state) => ({
    ...state,
    entries: [],
    storageBytes: 0,
    storageNotice: null,
    isInitialized: false
  }));

  try {
    await clearAllEntries();
  } catch (error) {
    console.warn('Failed to clear history for new browser session', error);
  }

  markHistorySession();
}

if (typeof window !== 'undefined') {
  void (async () => {
    await resetHistoryForNewSession();
    await useHistoryStore.getState().initialize();
  })();
}

export const selectHistoryEntries = (state: HistoryState) => state.entries;
export const selectHistoryFilters = (state: HistoryState) => state.filters;
